import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  Lock as LockIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getEmailErrorMessage,
  getPhoneNumberErrorMessage,
  PASSWORD_NO_RECENT_REUSE_HINT,
  getPasswordErrorMessage,
  normalizeTenDigitPhone,
} from '../utils/validation';
import AuthService from '../services/authService';
import VerificationService from '../services/verificationService';

type VerificationMethod = 'phone' | 'email';

const initialFromLocation = (state: unknown): { method: VerificationMethod; phone: string; email: string } => {
  const s = (state ?? {}) as { identifier?: string; phoneNumber?: string };
  const raw = (s.identifier ?? s.phoneNumber ?? '').trim();
  if (!raw) return { method: 'phone', phone: '', email: '' };
  if (raw.includes('@')) {
    return { method: 'email', phone: '', email: raw };
  }
  return { method: 'phone', phone: normalizeTenDigitPhone(raw), email: '' };
};

const ForgotPassword: React.FC = () => {
  const location = useLocation();
  const init = initialFromLocation(location.state);
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState(init.phone);
  const [email, setEmail] = useState(init.email);
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>(init.method);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [step, setStep] = useState<'method' | 'otp' | 'newPassword'>('method');
  const [otpVerified, setOtpVerified] = useState(false);

  const handleMethodChange = (event: React.MouseEvent<HTMLElement>, newMethod: VerificationMethod | null) => {
    if (newMethod !== null) {
      setVerificationMethod(newMethod);
      setError('');
      setPhoneError('');
      setEmailError('');
    }
  };

  const handleSendOtp = async () => {
    setError('');
    setPhoneError('');
    setEmailError('');

    if (verificationMethod === 'phone') {
      const normalized = normalizeTenDigitPhone(phoneNumber);
      const phoneErr = getPhoneNumberErrorMessage(normalized);
      setPhoneError(phoneErr);
      if (phoneErr || !normalized) {
        setError(phoneErr || 'Please enter your phone number');
        return;
      }

      try {
        setLoading(true);
        await VerificationService.sendPasswordResetPhoneOtp(normalized);
        setStep('otp');
      } catch (err: any) {
        console.error('Error sending phone OTP:', err);
        setError(err?.response?.data?.error || 'Failed to send OTP. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      const emailErr = getEmailErrorMessage(email);
      setEmailError(emailErr);
      if (emailErr || !email.trim()) {
        setError(emailErr || 'Please enter your email address');
        return;
      }

      try {
        setLoading(true);
        await VerificationService.sendPasswordResetEmailOtp(email.trim());
        setStep('otp');
      } catch (err: any) {
        console.error('Error sending email OTP:', err);
        setError(err?.response?.data?.error || 'Failed to send OTP. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      let ok = false;
      if (verificationMethod === 'phone') {
        const res = await VerificationService.verifyPasswordResetPhoneOtp(
          normalizeTenDigitPhone(phoneNumber),
          otp.trim()
        );
        ok = res?.verified === true;
      } else {
        const res = await VerificationService.verifyPasswordResetEmailOtp(email.trim(), otp.trim());
        ok = res?.verified === true;
      }
      if (!ok) {
        setError('Invalid or expired OTP. Please try again.');
        return;
      }
      setOtpVerified(true);
      setStep('newPassword');
    } catch (err: any) {
      console.error('OTP verify failed:', err);
      setError(err?.response?.data?.error || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    const pwdErr = getPasswordErrorMessage(newPassword);
    if (pwdErr) {
      setError(pwdErr);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const identifier =
        verificationMethod === 'phone' ? normalizeTenDigitPhone(phoneNumber) : email.trim();

      await AuthService.resetPassword(identifier, verificationMethod, newPassword);

      setError('');
      alert('Password reset successful! Please login with your new password.');
      navigate('/login');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box>
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 4, md: 5 },
              width: '100%',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: '0 4px 20px rgba(245, 87, 108, 0.4)',
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>

              <Typography
                component="h1"
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Reset Password
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 3 }}
              >
                Verify your identity and create a new password
              </Typography>

              {error && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Alert
                    severity="error"
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
                    }}
                  >
                    {error}
                  </Alert>
                </Box>
              )}

              {step === 'method' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center' }}>
                    Choose verification method:
                  </Typography>
                  <ToggleButtonGroup
                    value={verificationMethod}
                    exclusive
                    onChange={handleMethodChange}
                    fullWidth
                    sx={{ mb: 3 }}
                  >
                    <ToggleButton value="phone" sx={{ py: 1.5 }}>
                      <PhoneIcon sx={{ mr: 1 }} />
                      Phone
                    </ToggleButton>
                    <ToggleButton value="email" sx={{ py: 1.5 }}>
                      <EmailIcon sx={{ mr: 1 }} />
                      Email
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {verificationMethod === 'phone' ? (
                    <TextField
                      fullWidth
                      margin="normal"
                      required
                      id="phoneNumber"
                      label="Phone Number"
                      name="phoneNumber"
                      autoComplete="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(digits);
                        setPhoneError(getPhoneNumberErrorMessage(digits));
                      }}
                      error={!!phoneError}
                      helperText={phoneError}
                      placeholder="10-digit mobile number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      margin="normal"
                      required
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(getEmailErrorMessage(e.target.value));
                      }}
                      error={!!emailError}
                      helperText={emailError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading || (verificationMethod === 'phone' ? !!phoneError || !normalizeTenDigitPhone(phoneNumber) : !!emailError || !email.trim())}
                    onClick={handleSendOtp}
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                  </Button>
                </Box>
              )}

              {step === 'otp' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center' }}>
                    Enter OTP sent to {verificationMethod === 'phone' ? phoneNumber : email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Enter the 4-digit code from your SMS or email (in development the server may use a fixed test code).
                  </Typography>
                  <TextField
                    fullWidth
                    margin="normal"
                    required
                    id="otp"
                    label="OTP"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\s/g, '').replace(/\D/g, '').slice(0, 4))}
                    inputProps={{
                      maxLength: 4,
                      inputMode: 'numeric',
                      autoComplete: 'one-time-code',
                    }}
                    autoFocus
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading || otp.trim().length !== 4}
                    onClick={() => void handleVerifyOtp()}
                    sx={{
                      mt: 2,
                      mb: 1,
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setStep('method');
                      setOtp('');
                    }}
                  >
                    Back
                  </Button>
                </Box>
              )}

              {step === 'newPassword' && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center' }}>
                    Create New Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center', lineHeight: 1.45 }}>
                    {PASSWORD_NO_RECENT_REUSE_HINT}
                  </Typography>
                  {otpVerified && (
                    <Chip
                      label="OTP Verified ✓"
                      color="success"
                      sx={{ mb: 2, width: '100%' }}
                    />
                  )}
                  <TextField
                    fullWidth
                    margin="normal"
                    required
                    name="newPassword"
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    required
                    name="confirmPassword"
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading || !newPassword || !confirmPassword}
                    onClick={handleResetPassword}
                    sx={{
                      mt: 3,
                      mb: 1,
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setStep('otp');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    Back
                  </Button>
                </Box>
              )}

              <Box sx={{ mt: 2, width: '100%' }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/login')}
                  sx={{ color: 'primary.main' }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
